import React, { useEffect, useMemo, useRef, useState } from "react";
import { LayoutDashboard, Loader2, CheckCircle2, Clock, Euro, TrendingUp, Wallet, Hourglass, X, ListTodo, Plus, Trash2, Circle, Target, Pencil, Check, PartyPopper, FileSpreadsheet } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../lib/api";
import { toast } from "sonner";
import Referents from "./Referents";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("fr-FR")} €`;


const KPI_DEFS = [
  { key: "completed_count", label: "Missions réalisées", icon: CheckCircle2, color: "text-emerald-600", money: false, hint: "Missions complétées sur la période" },
  { key: "in_progress_count", label: "Missions en cours", icon: Hourglass, color: "text-indigo-600", money: false, hint: "Missions acceptées, en attente ou à exécuter" },
  { key: "revenue_realized_period", label: "Revenus réalisés (période)", icon: Euro, color: "text-brand-greenDeep", money: true, hint: "Commissions des missions complétées sur la période" },
  { key: "revenue_realized_alltime", label: "Revenus réalisés (depuis le début)", icon: TrendingUp, color: "text-brand-greenDeep", money: true, hint: "Toutes vos commissions acquises" },
  { key: "revenue_in_progress", label: "Revenus des missions en cours", icon: Wallet, color: "text-blue-600", money: true, hint: "Montant à percevoir des missions en cours" },
  { key: "revenue_potential", label: "Revenus potentiels", icon: Clock, color: "text-amber-600", money: true, hint: "Missions qui vous sont assignées mais non encore acceptées" },
];

function KpiCard({ def, value, onClick }) {
  const Icon = def.icon;
  return (
    <button onClick={onClick} className="text-left bg-white rounded-3xl border border-brand-soft p-5 hover:border-brand-greenDeep hover:shadow-sm transition-all group" data-testid={`kpi-${def.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-brand-green/55 flex items-center gap-1.5"><Icon className={`w-4 h-4 ${def.color}`} />{def.label}</p>
      </div>
      <p className={`font-display text-3xl font-semibold mt-2 ${def.color}`}>{def.money ? fmtEur(value) : (value || 0)}</p>
      <p className="text-[11px] text-brand-green/40 mt-1 group-hover:text-brand-greenDeep transition-colors">{def.hint} · cliquer pour le détail</p>
    </button>
  );
}

function DrillModal({ def, rows, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="kpi-drill-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-soft">
          <h3 className="font-display text-lg font-semibold text-brand-greenDeep">{def.label} — détail ({rows.length})</h3>
          <button onClick={onClose} data-testid="kpi-drill-close"><X className="w-5 h-5 text-brand-green/50" /></button>
        </div>
        <div className="overflow-y-auto">
          {rows.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-brand-green/45" data-testid="kpi-drill-empty">Aucune mission dans cette catégorie.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-brand-softer text-brand-green/60 sticky top-0"><tr>
                <th className="text-left px-5 py-2.5 font-semibold">Mission</th>
                <th className="text-left px-5 py-2.5 font-semibold">Bien</th>
                <th className="text-left px-5 py-2.5 font-semibold">Date</th>
                <th className="text-left px-5 py-2.5 font-semibold">Statut</th>
                <th className="text-right px-5 py-2.5 font-semibold">Montant</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-brand-soft" data-testid={`kpi-drill-row-${r.id}`}>
                    <td className="px-5 py-2.5 text-brand-greenDeep font-medium">{r.mission_label}</td>
                    <td className="px-5 py-2.5 text-brand-green/70">{r.property_name}</td>
                    <td className="px-5 py-2.5 text-brand-green/70">{r.date || "—"}</td>
                    <td className="px-5 py-2.5 text-brand-green/70">{r.status_label}</td>
                    <td className="px-5 py-2.5 text-right font-semibold text-brand-greenDeep">{fmtEur(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function TasksBlock() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referents/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!title.trim()) { toast.error("Intitulé requis"); return; }
    setBusy(true);
    try { await api.post("/referents/tasks", { title, due }); setTitle(""); setDue(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const toggle = async (t) => { try { await api.put(`/referents/tasks/${t.id}/toggle`); load(); } catch { toast.error("Erreur"); } };
  const del = async (t) => { try { await api.delete(`/referents/tasks/${t.id}`); load(); } catch { toast.error("Erreur"); } };

  const pending = tasks.filter((t) => !t.done);
  const done = tasks.filter((t) => t.done);
  const inp = "px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";

  return (
    <div className="bg-white rounded-3xl border border-brand-soft p-6" data-testid="referent-tasks-block">
      <div className="flex items-center gap-2 mb-1"><ListTodo className="w-5 h-5 text-brand-greenDeep" /><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Tâches à venir</h3>{pending.length > 0 && <span className="text-xs font-bold bg-brand-lime/40 text-brand-greenDeep rounded-full px-2 py-0.5">{pending.length}</span>}</div>
      <p className="text-sm text-brand-green/55 mb-4">Actions générées automatiquement à partir de vos missions, plus vos propres rappels.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ajouter une tâche…" className={`${inp} flex-1 min-w-[200px]`} data-testid="task-input" />
        <input type="date" value={due} onChange={(e) => setDue(e.target.value)} className={inp} data-testid="task-due-input" />
        <button onClick={add} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold disabled:opacity-60" data-testid="task-add-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8 text-brand-green/40"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : pending.length === 0 && done.length === 0 ? (
        <p className="text-sm text-brand-green/45 py-4" data-testid="tasks-empty">Aucune tâche pour le moment. Vos missions à traiter apparaîtront ici automatiquement.</p>
      ) : (
        <div className="space-y-1.5">
          {pending.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-brand-softer" data-testid={`task-${t.id}`}>
              <button onClick={() => toggle(t)} className="text-brand-green/40 hover:text-emerald-600" data-testid={`task-toggle-${t.id}`}><Circle className="w-5 h-5" /></button>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-brand-greenDeep truncate">{t.title}</p>
                {(t.due || t.source === "system") && <p className="text-[11px] text-brand-green/45">{t.source === "system" ? "Auto · " : ""}{t.due ? `échéance ${t.due}` : ""}</p>}
              </div>
              <button onClick={() => del(t)} className="text-brand-green/40 hover:text-red-500" data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {done.map((t) => (
            <div key={t.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl opacity-60" data-testid={`task-${t.id}`}>
              <button onClick={() => toggle(t)} className="text-emerald-600" data-testid={`task-toggle-${t.id}`}><CheckCircle2 className="w-5 h-5" /></button>
              <p className="flex-1 min-w-0 text-sm text-brand-green/50 line-through truncate">{t.title}</p>
              <button onClick={() => del(t)} className="text-brand-green/40 hover:text-red-500" data-testid={`task-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CONFETTI_COLORS = ["#c8e6a0", "#8bbf3f", "#1f4d2e", "#e8c15a", "#f4a259", "#7fbf7f", "#d97706"];

function Confetti({ show }) {
  const pieces = useMemo(() => Array.from({ length: 54 }, (_, i) => ({
    id: i,
    x: (Math.random() * 2 - 1) * 380,
    delay: Math.random() * 0.3,
    rot: Math.random() * 900 - 450,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    w: 6 + Math.random() * 8,
    h: 8 + Math.random() * 10,
    dur: 1.7 + Math.random() * 1.3,
  })), []);
  return (
    <AnimatePresence>
      {show && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden z-10" aria-hidden data-testid="goal-confetti">
          {pieces.map((p) => (
            <motion.span key={p.id}
              initial={{ opacity: 1, x: 0, y: -12, rotate: 0 }}
              animate={{ opacity: [1, 1, 0], x: p.x, y: 220, rotate: p.rot }}
              transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
              style={{ position: "absolute", left: "50%", top: 10, width: p.w, height: p.h, background: p.color, borderRadius: 2 }}
            />
          ))}
        </div>
      )}
    </AnimatePresence>
  );
}

function GoalGauge({ goal, revenue, monthLabel, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(goal || 0);
  const [busy, setBusy] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  useEffect(() => { setVal(goal || 0); }, [goal]);
  const pct = goal > 0 ? Math.min(100, Math.round((revenue / goal) * 100)) : 0;
  const reached = goal > 0 && revenue >= goal;
  const fire = () => { setCelebrate(false); requestAnimationFrame(() => setCelebrate(true)); setTimeout(() => setCelebrate(false), 3200); };
  const prevReached = useRef(reached);
  useEffect(() => {
    if (reached && monthLabel) {
      const key = `ref_goal_celebrated_${monthLabel}`;
      if (!prevReached.current || !localStorage.getItem(key)) { localStorage.setItem(key, "1"); fire(); }
    }
    prevReached.current = reached;
  }, [reached, monthLabel]);
  const save = async () => {
    setBusy(true);
    try { await api.put("/referents/me/goal", { monthly_goal: Number(val) || 0 }); toast.success("Objectif mensuel mis à jour"); setEditing(false); onSaved && onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  return (
    <div className={`relative overflow-hidden bg-white rounded-3xl border p-6 transition-colors ${reached ? "border-emerald-300" : "border-brand-soft"}`} data-testid="goal-gauge">
      <Confetti show={celebrate} />
      <div className="flex items-center justify-between gap-2 mb-1 relative z-0">
        <div className="flex items-center gap-2"><Target className="w-5 h-5 text-brand-greenDeep" /><h3 className="font-display text-lg font-semibold text-brand-greenDeep">Objectif mensuel</h3><span className="text-xs text-brand-green/45">· {monthLabel}</span></div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-greenDeep hover:text-brand-green" data-testid="goal-edit-btn"><Pencil className="w-3.5 h-3.5" /> Modifier</button>
        ) : (
          <div className="flex items-center gap-2">
            <input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} className="w-28 px-3 py-1.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep" data-testid="goal-input" />
            <span className="text-sm text-brand-green/55">€</span>
            <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-greenDeep text-white text-xs font-semibold disabled:opacity-60" data-testid="goal-save-btn">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Enregistrer</button>
          </div>
        )}
      </div>
      {goal > 0 ? (
        <>
          <div className="flex items-end justify-between mt-3 mb-1.5 relative z-0">
            <p className="text-sm text-brand-green/60">Revenus réalisés ce mois-ci</p>
            <p className={`font-display text-2xl font-semibold ${reached ? "text-emerald-600" : "text-brand-greenDeep"}`}>{fmtEur(revenue)} <span className="text-sm font-normal text-brand-green/45">/ {fmtEur(goal)}</span></p>
          </div>
          <div className="h-3 rounded-full bg-brand-softer overflow-hidden relative z-0" data-testid="goal-bar">
            <motion.div className={`h-full rounded-full ${reached ? "bg-emerald-500" : "bg-brand-limeDark"}`} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7, ease: "easeOut" }} />
          </div>
          {reached ? (
            <motion.button onClick={fire} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
              className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-bold relative z-0" data-testid="goal-reached-badge" title="Rejouer l'animation">
              <PartyPopper className="w-4 h-4" /> Objectif atteint — bravo ! ({pct}%)
            </motion.button>
          ) : (
            <p className="text-xs text-brand-green/50 mt-1.5 relative z-0" data-testid="goal-pct">{pct}% de votre objectif · reste {fmtEur(Math.max(0, goal - revenue))}</p>
          )}
        </>
      ) : (
        <p className="text-sm text-brand-green/45 mt-3 relative z-0" data-testid="goal-empty">Définissez un objectif de revenus mensuel pour suivre votre progression.</p>
      )}
    </div>
  );
}

export default function ReferentDashboard() {
  const [period, setPeriod] = useState("this_year");
  const [year, setYear] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null);
  const [exporting, setExporting] = useState(false);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params = period === "year" ? { period: "year", year } : { period };
      const res = await api.get("/referents/dashboard-export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenus_referent_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export Excel téléchargé");
    } catch {
      toast.error("Export impossible");
    } finally { setExporting(false); }
  };

  const load = () => {
    setLoading(true);
    const params = period === "year" ? { period: "year", year } : { period };
    api.get("/referents/dashboard-stats", { params }).then((r) => setData(r.data)).catch(() => toast.error("Chargement des indicateurs impossible")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, year]);

  const years = data?.years || [];
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(() => {
    const set = new Set(years);
    for (let y = currentYear; y >= currentYear - 4; y--) set.add(String(y));
    return [...set].sort().reverse();
  }, [years, currentYear]);

  const onPeriodChange = (val) => {
    if (val.startsWith("year:")) { setPeriod("year"); setYear(val.slice(5)); }
    else { setPeriod(val); setYear(""); }
  };
  const periodValue = period === "year" ? `year:${year}` : period;

  return (
    <div className="space-y-8" data-testid="referent-dashboard-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-brand-lime/30 text-brand-greenDeep flex items-center justify-center"><LayoutDashboard className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-2xl font-semibold text-brand-greenDeep">Tableau de bord</h2>
            <p className="text-sm text-brand-green/55">Vue d'ensemble de votre activité de référent</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportExcel} disabled={exporting} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors disabled:opacity-60" data-testid="export-revenue-excel-btn" title="Exporter les revenus réalisés (période + depuis le début) en Excel">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Exporter (Excel)
          </button>
          <label className="flex items-center gap-2">
            <span className="text-xs text-brand-green/55">Période</span>
            <select value={periodValue} onChange={(e) => onPeriodChange(e.target.value)} className="px-3 py-2 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep font-medium" data-testid="period-select">
              <option value="this_year">Année en cours</option>
              <option value="12m">12 derniers mois</option>
              <option value="all">Depuis le début</option>
              <optgroup label="Année précise">
                {yearOptions.map((y) => <option key={y} value={`year:${y}`}>{y}</option>)}
              </optgroup>
            </select>
          </label>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-16 text-brand-green/50"><Loader2 className="w-7 h-7 animate-spin" /></div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kpi-grid">
          {KPI_DEFS.map((def) => (
            <KpiCard key={def.key} def={def} value={data.kpis[def.key]} onClick={() => setDrill(def)} />
          ))}
        </div>
      )}

      {data && <GoalGauge goal={data.monthly_goal || 0} revenue={data.month_revenue || 0} monthLabel={data.month_label || ""} onSaved={load} />}

      <TasksBlock />

      <div className="pt-2 border-t border-brand-soft" data-testid="dashboard-missions-embed">
        <Referents view="missions" />
      </div>

      {drill && <DrillModal def={drill} rows={(data?.drill || {})[drill.key] || []} onClose={() => setDrill(null)} />}
    </div>
  );
}
